import { EstatePage_EntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import { EntityInEstateStatus, EntityKind, EntityStage } from '@/types/schema';

import {
  TRUST_KINDS_BY_VARIANT,
  TrustContainerVariant,
} from './TrustContainer.types';

export function filterTrustsByVariant(
  entities: EstatePage_EntityFragment[],
  variant: TrustContainerVariant
): EstatePage_EntityFragment[] {
  return entities.filter((entity) => {
    // Only show active and completed entities, exclude drafts and pre-created
    if (
      entity.stage !== EntityStage.Active &&
      entity.stage !== EntityStage.Completed
    ) {
      return false;
    }

    // Get the entity's actual in-estate status
    const entityInEstateStatus = getEntityInEstateStatus(entity);

    // Filter based on variant and in-estate status
    return shouldShowTrustInVariant(entity.kind, entityInEstateStatus, variant);
  });
}

export function getAllTrusts(
  entities: EstatePage_EntityFragment[]
): EstatePage_EntityFragment[] {
  const allTrustKinds = Object.values(TRUST_KINDS_BY_VARIANT).flat();

  return entities.filter((entity) => {
    // Only show active and completed entities, exclude drafts and pre-created
    if (
      entity.stage !== EntityStage.Active &&
      entity.stage !== EntityStage.Completed
    ) {
      return false;
    }

    return allTrustKinds.includes(entity.kind);
  });
}

/**
 * Get the entity's in-estate status from various possible locations in the data
 */
function getEntityInEstateStatus(
  entity: EstatePage_EntityFragment
): EntityInEstateStatus | null {
  // Check different entity subtypes for inEstateStatus
  if (entity.subtype && 'inEstateStatus' in entity.subtype) {
    const subtype = entity.subtype as {
      inEstateStatus?: EntityInEstateStatus | null;
    };
    if (subtype.inEstateStatus) {
      return subtype.inEstateStatus;
    }
  }

  // Check for inEstateStatusRequired field (for some entity types)
  if (entity.subtype && 'inEstateStatusRequired' in entity.subtype) {
    const subtype = entity.subtype as {
      inEstateStatusRequired?: EntityInEstateStatus | null;
    };
    if (subtype.inEstateStatusRequired) {
      return subtype.inEstateStatusRequired;
    }
  }

  return null;
}

/**
 * Determine if a trust should be shown in a specific variant based on its kind and in-estate status
 */
function shouldShowTrustInVariant(
  entityKind: EntityKind,
  inEstateStatus: EntityInEstateStatus | null,
  variant: TrustContainerVariant
): boolean {
  // For Irrevocable, SLAT, ILIT, CLT, GRAT, QPRT - show based on inEstateStatus
  if (
    [
      EntityKind.RevocableTrust,
      EntityKind.IrrevocableTrust,
      EntityKind.SlatTrust,
      EntityKind.IlitTrust,
      EntityKind.CltTrust,
      EntityKind.GratTrust,
      EntityKind.QprtTrust,
    ].includes(entityKind)
  ) {
    if (inEstateStatus === EntityInEstateStatus.InEstate) {
      return variant === 'in-estate';
    } else if (entityKind === EntityKind.CltTrust) {
      return variant === 'out-of-estate-charity';
    } else if (inEstateStatus === EntityInEstateStatus.OutOfEstate) {
      return variant === 'out-of-estate-family';
    }
  }

  // For charitable entities (CRT, DAF, Private Foundation), always show in charity variant
  if (
    [
      EntityKind.CrtTrust,
      EntityKind.DonorAdvisedFund,
      EntityKind.PrivateFoundation,
    ].includes(entityKind)
  ) {
    return variant === 'out-of-estate-charity';
  }

  // Fallback to original behavior if inEstateStatus is null/undefined
  if (!inEstateStatus) {
    const trustKindsForVariant = TRUST_KINDS_BY_VARIANT[variant];
    return trustKindsForVariant.includes(entityKind);
  }

  return false;
}
